/**
 * EU Taxonomy Page — Activities + Assessments
 *
 * Tab 1: NACE Activities table from GET /api/v1/impact-core/taxonomy/activities
 * Tab 2: Taxonomy Assessments from GET /api/v1/impact-core/taxonomy/assessments/
 * Fund alignment summary via GET /api/v1/impact-core/taxonomy/alignment/{fund_id}
 *
 * API response for /activities is a dict keyed by NACE code, not an array.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  GitBranch,
  ClipboardList,
  Search,
  ChevronDown,
  BarChart3,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useTranslation } from '@/i18n';
import type { Fund } from '@/types';

// ─── Types ──────────────────────────────────────────────────────

interface NACEActivity {
  code: string;
  name: string;
  description?: string;
  objectives?: string[];
  sector?: string;
}

interface TaxonomyAssessment {
  id: number;
  fund_id?: number;
  fund_name?: string;
  investee_id?: number;
  investee_name?: string;
  nace_code: string;
  activity_name?: string;
  objective: string;
  aligned: boolean;
  alignment_percentage?: number;
  substantial_contribution?: boolean;
  do_no_significant_harm?: boolean;
  minimum_safeguards?: boolean;
  notes?: string;
  created_at?: string;
}

interface FundAlignment {
  fund_id: number;
  fund_name: string;
  total_investments: number;
  aligned_investments: number;
  alignment_percentage: number;
  revenue_aligned_pct?: number;
  capex_aligned_pct?: number;
  opex_aligned_pct?: number;
}

type TabKey = 'activities' | 'assessments';

// ─── Main Component ─────────────────────────────────────────────

export function TaxonomyPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('activities');
  const [activitySearch, setActivitySearch] = useState('');
  const [selectedFundId, setSelectedFundId] = useState('');

  // Fetch NACE activities (dict keyed by code)
  const activitiesQuery = useQuery<Record<string, Omit<NACEActivity, 'code'>>>({
    queryKey: ['ic', 'taxonomy', 'activities'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/taxonomy/activities');
      return res.data?.data ?? res.data ?? {};
    },
    staleTime: 300_000, // 5 min — NACE codes are static
    enabled: activeTab === 'activities',
  });

  // Fetch assessments
  const assessmentsQuery = useQuery<TaxonomyAssessment[]>({
    queryKey: ['ic', 'taxonomy', 'assessments'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/taxonomy/assessments/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
    enabled: activeTab === 'assessments',
  });

  // Fetch funds for alignment selector
  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  // Fetch fund alignment when a fund is selected
  const alignmentQuery = useQuery<FundAlignment>({
    queryKey: ['ic', 'taxonomy', 'alignment', selectedFundId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/taxonomy/alignment/${selectedFundId}`);
      return res.data?.data ?? res.data;
    },
    enabled: activeTab === 'assessments' && !!selectedFundId,
    staleTime: 30_000,
  });

  const funds = fundsQuery.data ?? [];

  // Convert activities dict to array
  const activities: NACEActivity[] = useMemo(() => {
    const raw = activitiesQuery.data;
    if (!raw || typeof raw !== 'object') return [];
    return Object.entries(raw).map(([code, info]) => ({
      code,
      ...(typeof info === 'object' && info !== null ? info : { name: String(info) }),
    }));
  }, [activitiesQuery.data]);

  // Filter activities by search
  const filteredActivities = useMemo(() => {
    if (!activitySearch) return activities;
    const q = activitySearch.toLowerCase();
    return activities.filter(
      (a) =>
        a.code.toLowerCase().includes(q) ||
        (a.name ?? '').toLowerCase().includes(q) ||
        (a.sector ?? '').toLowerCase().includes(q) ||
        (a.description ?? '').toLowerCase().includes(q)
    );
  }, [activities, activitySearch]);

  const assessments = assessmentsQuery.data ?? [];
  const alignment = alignmentQuery.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">{t('pages.taxonomyTitle')}</h1>
        <p className="text-xs text-ic-text-tertiary">
          NACE activity classification and taxonomy alignment assessments
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-ic-border">
        <div className="flex gap-0">
          <TabButton
            label="Activities"
            icon={<GitBranch className="h-3.5 w-3.5" />}
            active={activeTab === 'activities'}
            onClick={() => setActiveTab('activities')}
          />
          <TabButton
            label="Assessments"
            icon={<ClipboardList className="h-3.5 w-3.5" />}
            active={activeTab === 'assessments'}
            onClick={() => setActiveTab('assessments')}
          />
        </div>
      </div>

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary" />
            <input
              type="text"
              placeholder="Search NACE codes, activities, sectors..."
              value={activitySearch}
              onChange={(e) => setActivitySearch(e.target.value)}
              className="w-full rounded-md border border-ic-border bg-ic-input py-2 pl-9 pr-3 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
            {activitiesQuery.isLoading ? (
              <LoadingSkeleton rows={8} />
            ) : activitiesQuery.isError ? (
              <ErrorState message="Failed to load taxonomy activities." />
            ) : filteredActivities.length === 0 ? (
              <EmptyState message={activities.length === 0 ? 'No NACE activities available.' : 'No activities match your search.'} />
            ) : (
              <table className="w-full text-xs ic-table">
                <thead>
                  <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                    <th className="px-4 pb-2 pt-3 font-medium">NACE Code</th>
                    <th className="px-4 pb-2 pt-3 font-medium">Activity Name</th>
                    <th className="px-4 pb-2 pt-3 font-medium">Sector</th>
                    <th className="px-4 pb-2 pt-3 font-medium">Objectives</th>
                    <th className="px-4 pb-2 pt-3 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivities.map((activity) => (
                    <tr key={activity.code} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                      <td className="py-2.5 px-4 font-mono text-xs font-medium text-ic-accent">
                        {activity.code}
                      </td>
                      <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">
                        {activity.name ?? '--'}
                      </td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">
                        {activity.sector ?? '--'}
                      </td>
                      <td className="py-2.5 px-4">
                        <div className="flex flex-wrap gap-1">
                          {(activity.objectives ?? []).map((obj) => (
                            <span
                              key={obj}
                              className="inline-block rounded bg-ic-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-ic-accent"
                            >
                              {obj}
                            </span>
                          ))}
                          {(!activity.objectives || activity.objectives.length === 0) && (
                            <span className="text-xs text-ic-text-tertiary">--</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-tertiary max-w-xs truncate">
                        {activity.description ?? '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!activitiesQuery.isLoading && filteredActivities.length > 0 && (
            <p className="text-[10px] text-ic-text-tertiary">
              Showing {filteredActivities.length} of {activities.length} activit{activities.length === 1 ? 'y' : 'ies'}
            </p>
          )}
        </div>
      )}

      {/* Assessments Tab */}
      {activeTab === 'assessments' && (
        <div className="space-y-4">
          {/* Fund Selector + Alignment Summary */}
          <div className="flex flex-wrap items-start gap-4">
            <div className="relative min-w-[200px] max-w-sm">
              <select
                value={selectedFundId}
                onChange={(e) => setSelectedFundId(e.target.value)}
                className="w-full appearance-none rounded-md border border-ic-border bg-ic-input px-3 py-2 pr-8 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">All funds (select for alignment)...</option>
                {funds.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
            </div>

            {/* Alignment Summary Card */}
            {selectedFundId && alignment && (
              <div className="flex items-center gap-4 rounded-lg border border-ic-border bg-ic-card px-4 py-3">
                <BarChart3 className="h-4 w-4 text-ic-accent shrink-0" />
                <div className="flex items-center gap-6">
                  <AlignmentMetric label="Alignment" value={`${(alignment.alignment_percentage ?? 0).toFixed(1)}%`} />
                  <AlignmentMetric label="Aligned" value={`${alignment.aligned_investments ?? 0} / ${alignment.total_investments ?? 0}`} />
                  {alignment.revenue_aligned_pct != null && (
                    <AlignmentMetric label="Revenue" value={`${alignment.revenue_aligned_pct.toFixed(1)}%`} />
                  )}
                  {alignment.capex_aligned_pct != null && (
                    <AlignmentMetric label="CapEx" value={`${alignment.capex_aligned_pct.toFixed(1)}%`} />
                  )}
                  {alignment.opex_aligned_pct != null && (
                    <AlignmentMetric label="OpEx" value={`${alignment.opex_aligned_pct.toFixed(1)}%`} />
                  )}
                </div>
              </div>
            )}

            {selectedFundId && alignmentQuery.isLoading && (
              <div className="flex items-center gap-2 rounded-lg border border-ic-border bg-ic-card px-4 py-3">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-ic-accent border-t-transparent" />
                <span className="text-xs text-ic-text-tertiary">Loading alignment...</span>
              </div>
            )}
          </div>

          {/* Assessments Table */}
          <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
            {assessmentsQuery.isLoading ? (
              <LoadingSkeleton rows={5} />
            ) : assessmentsQuery.isError ? (
              <ErrorState message="Failed to load taxonomy assessments." />
            ) : assessments.length === 0 ? (
              <EmptyState message="No taxonomy assessments found." />
            ) : (
              <table className="w-full text-xs ic-table">
                <thead>
                  <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                    <th className="px-4 pb-2 pt-3 font-medium">Fund</th>
                    <th className="px-4 pb-2 pt-3 font-medium">Investee</th>
                    <th className="px-4 pb-2 pt-3 font-medium">NACE Code</th>
                    <th className="px-4 pb-2 pt-3 font-medium">Activity</th>
                    <th className="px-4 pb-2 pt-3 font-medium">Objective</th>
                    <th className="px-4 pb-2 pt-3 font-medium text-center">SC</th>
                    <th className="px-4 pb-2 pt-3 font-medium text-center">DNSH</th>
                    <th className="px-4 pb-2 pt-3 font-medium text-center">MS</th>
                    <th className="px-4 pb-2 pt-3 font-medium text-center">Aligned</th>
                    <th className="px-4 pb-2 pt-3 font-medium text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map((a) => (
                    <tr key={a.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{a.fund_name ?? '--'}</td>
                      <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{a.investee_name ?? '--'}</td>
                      <td className="py-2.5 px-4 font-mono text-xs text-ic-accent">{a.nace_code}</td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{a.activity_name ?? '--'}</td>
                      <td className="py-2.5 px-4">
                        <StatusBadge label={a.objective ?? '--'} variant="info" />
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <CriteriaIcon met={a.substantial_contribution} />
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <CriteriaIcon met={a.do_no_significant_harm} />
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <CriteriaIcon met={a.minimum_safeguards} />
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <StatusBadge
                          label={a.aligned ? 'Yes' : 'No'}
                          variant={a.aligned ? 'success' : 'default'}
                        />
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono text-xs text-ic-text-primary">
                        {a.alignment_percentage != null ? `${a.alignment_percentage}%` : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────

function TabButton({ label, icon, active, onClick }: { label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
        active
          ? 'border-ic-accent text-ic-accent'
          : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function AlignmentMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <p className="text-[10px] text-ic-text-tertiary">{label}</p>
      <p className="text-xs font-semibold text-ic-text-primary">{value}</p>
    </div>
  );
}

function CriteriaIcon({ met }: { met?: boolean }) {
  if (met === true) {
    return <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" title="Met" />;
  }
  if (met === false) {
    return <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" title="Not met" />;
  }
  return <span className="text-xs text-ic-text-tertiary">--</span>;
}

function LoadingSkeleton({ rows }: { rows: number }) {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />
      ))}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex h-48 items-center justify-center">
      <p className="text-xs text-red-400">{message}</p>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-48 items-center justify-center">
      <p className="text-xs text-ic-text-tertiary">{message}</p>
    </div>
  );
}
